// Custom vars on tasks (docs/plans/2026-10-02-borden-ontwerp.md §3.2, §6): a flat
// key → value map external systems set and search on. Goblic does nothing else with it.

import { z } from 'zod'

export type CustomVarValue = string | number | boolean
export type CustomVars = Record<string, CustomVarValue>
/** PATCH: a null value deletes the key. */
export type CustomVarsPatch = Record<string, CustomVarValue | null>

export const CUSTOM_VAR_KEY_RE = /^[A-Za-z][A-Za-z0-9_.-]{0,63}$/
export const CUSTOM_VARS_MAX_KEYS = 50
export const CUSTOM_VAR_STRING_MAX = 2000
export const CUSTOM_VARS_MAX_BYTES = 16_384 // JSON.stringify length of the whole map

export const CustomVarValueSchema: z.ZodType<CustomVarValue> = z.union([
  z.string().max(CUSTOM_VAR_STRING_MAX),
  z.number().finite(),
  z.boolean(),
])

const keyMessage = 'Custom var keys start with a letter and use letters, digits, "_", "." or "-" (max 64 characters)'

function checkKeys(map: Record<string, unknown>, ctx: z.RefinementCtx): void {
  const keys = Object.keys(map)
  if (keys.length > CUSTOM_VARS_MAX_KEYS) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: `At most ${CUSTOM_VARS_MAX_KEYS} custom vars are allowed` })
  }
  for (const key of keys) {
    if (!CUSTOM_VAR_KEY_RE.test(key)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: `${keyMessage}: "${key.slice(0, 80)}"`, path: [key] })
    }
  }
}

export const CustomVarsSchema: z.ZodType<CustomVars> = z
  .record(CustomVarValueSchema)
  .superRefine((map, ctx) => {
    checkKeys(map, ctx)
    if (JSON.stringify(map).length > CUSTOM_VARS_MAX_BYTES) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: `Custom vars may take at most ${CUSTOM_VARS_MAX_BYTES} bytes in total` })
    }
  })

export const CustomVarsPatchSchema: z.ZodType<CustomVarsPatch> = z
  .record(z.union([CustomVarValueSchema, z.null()]))
  .superRefine((map, ctx) => checkKeys(map, ctx))

/** Applies a patch (merge: null deletes; replace: the patch without nulls becomes the map). Throws a ZodError when the result breaks the limits. */
export function applyCustomVarsPatch(
  current: CustomVars,
  patch: CustomVarsPatch,
  mode: 'merge' | 'replace' = 'merge',
): CustomVars {
  const next: CustomVars = mode === 'replace' ? {} : { ...current }
  for (const [key, value] of Object.entries(patch)) {
    if (value === null) delete next[key]
    else next[key] = value
  }
  return CustomVarsSchema.parse(next)
}

/** `?customVars=` query value (a JSON object string) → validated CustomVars with ≥ 1 key. Throws a ZodError. */
export function parseCustomVarsQuery(raw: string): CustomVars {
  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch {
    throw new z.ZodError([{ code: z.ZodIssueCode.custom, message: 'customVars must be a JSON object', path: ['customVars'] }])
  }
  const vars = CustomVarsSchema.parse(parsed)
  if (Object.keys(vars).length === 0) {
    throw new z.ZodError([{ code: z.ZodIssueCode.custom, message: 'Give at least one custom var to match', path: ['customVars'] }])
  }
  return vars
}
